import { motion } from 'framer-motion';
import { Terminal, DollarSign, TrendingUp } from 'lucide-react';

const founders = [
  {
    name: 'Tyler',
    age: '21',
    title: 'Founder & CEO',
    focus: 'builds everything',
    icon: Terminal,
    filename: 'tyler.exe',
    quote: "I had the idea, built the vision, designed the pricing, and landed our first clients. I still build every site we ship. I don't stop until the work speaks for itself.",
    tags: ['FOUNDER', 'DESIGN', 'DEV'],
  },
  {
    name: 'Justin',
    age: '22',
    title: 'Co-Founder & CFO',
    focus: 'runs the numbers',
    icon: DollarSign,
    filename: 'justin.exe',
    quote: "I was there from day one. While Tyler built, I learned the money side—pricing, legal, keeping every dollar tracked. VortexWebCo doesn't just make money. It keeps it.",
    tags: ['COFOUNDER', 'FINANCE'],
  },
  {
    name: 'Jaiden',
    age: '',
    title: 'Co-Founder & CMO',
    focus: 'finds the clients',
    icon: TrendingUp,
    filename: 'jaiden.exe',
    quote: "By the time I joined, they already had something real. I came in as the missing piece—the guy who makes sure nobody sleeps on us. I don't wait for clients to find VortexWebCo. I find them first.",
    tags: ['COFOUNDER', 'GROWTH'],
  },
];

export const AboutSection = () => {
  return (
    <section id="about" className="py-28 md:py-36 bg-[#0a0a0a] relative overflow-hidden" data-testid="about-section">
      {/* Background */}
      <div className="absolute inset-0 cyber-grid opacity-[0.03]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="text-[#e62020] text-xs tracking-[0.2em] uppercase mb-5">
            The team
          </p>
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-bold uppercase leading-[0.95] text-white"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            data-testid="about-headline"
          >
            Three guys.
            <br />
            <span className="text-[#888888]">One mission.</span>
          </h2>
        </motion.div>

        {/* Founder Cards - asymmetric grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {founders.map((founder, index) => {
            // Varied timing and offsets
            const animDelay = [0, 0.1, 0.05][index];
            const yOffset = index === 1 ? 'lg:translate-y-6' : '';
            
            return (
              <motion.div
                key={founder.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.55, delay: animDelay, ease: 'easeOut' }}
                className={`group bg-[#111111] border border-[#1a1a1a] hover:border-[#262626] transition-colors ${yOffset}`}
                data-testid={`founder-card-${index}`}
              >
                {/* Terminal header */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#1a1a1a] bg-[#0d0d0d]">
                  <div className="flex items-center gap-2.5">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-[#e62020]/50" />
                      <div className="w-2 h-2 rounded-full bg-yellow-500/50" />
                      <div className="w-2 h-2 rounded-full bg-green-500/50" />
                    </div>
                    <span className="text-[9px] font-mono text-[#888888]">{founder.filename}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  {/* Name & Role */}
                  <div className="mb-4">
                    <div className="flex items-baseline gap-2">
                      <h3
                        className="text-2xl font-bold uppercase tracking-tight text-white"
                        style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                      >
                        {founder.name}
                      </h3>
                      {founder.age && (
                        <span className="text-[#888888] text-sm">{founder.age}</span>
                      )}
                    </div>
                    <p className="text-[#e62020] text-xs mt-1">
                      {founder.title} · <span className="text-[#888888]">{founder.focus}</span>
                    </p>
                  </div>

                  {/* Quote */}
                  <p className="text-[#888888] text-sm leading-relaxed mb-4">
                    "{founder.quote}"
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1.5">
                    {founder.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-[9px] tracking-wider text-[#888888] border border-[#1a1a1a] px-2 py-0.5 hover:border-[#e62020]/40 hover:text-[#e62020] transition-colors"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Mission - simpler */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-2xl"
        >
          <p className="text-lg md:text-xl text-white/90 leading-relaxed">
            We're not a big agency. We're three guys who kept seeing businesses with 
            great reviews and no websites—and decided to fix that.
          </p>
          <p className="text-[#e62020] mt-4 text-sm">
            We sacrifice. We grind. And we're just getting started.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
